import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  LayoutDashboard, Users2, GraduationCap, Wallet, CalendarCheck2, UserCog,
  Settings, Bell, Search, ChevronDown, LogOut, Menu, X, ClipboardCheck, CheckSquare, CalendarClock, Radio, Smartphone,
  PhoneCall, FlaskConical, School, MessageCircle, Coins, Megaphone, MapPin, BarChart3, GitBranch, Presentation,
  CalendarDays, Gift, TrendingUp, RefreshCw, BellRing, Crosshair, UserPlus, Trash2, Gauge,
  ClipboardList, Tv, PartyPopper, Map, FolderOpen,
} from "lucide-react";

const ROLE_LABELS = {
  DIRECTOR: "Director", CO_DIRECTOR: "Co-Director", HEAD_OFFICE: "Head Office",
  CENTER_MANAGER: "Center Manager", SENIOR_COUNSELLOR: "Senior Counsellor",
  COUNSELLOR: "Counsellor", MARKETING: "Marketing", FINANCE: "Finance",
  HR_ADMIN: "HR / Admin", FACULTY: "Faculty", DTP: "DTP / Operations",
  FRANCHISE: "Franchise Partner",
};

import { canAccess, canAccessCrm, hasCrm, leadLink } from "@/lib/roles";
import { useFollowupAlarms } from "@/lib/followupAlert";
import { usePerms } from "@/context/PermissionsContext";
import { useCrmSocket } from "@/pages/crm/socket";
import { LeadSearch } from "@/components/LeadSearch";
import { InstallAppButton } from "@/components/InstallApp";
import { OfflineStatus } from "@/components/OfflineStatus";

// Grouped navigation — ~40 screens folded into 6 clusters so the sidebar shows only a handful of
// group headers; the active group auto-opens. Role filtering still happens on NAV/CRM_NAV first;
// buildClusters only buckets the already-filtered items by their route.
const CLUSTER_DEFS = [
  { key: "work", label: "My Work", icon: LayoutDashboard, routes: ["/", "/crm", "/crm/myleads", "/crm/queue", "/crm/followups", "/tasks", "/crm/chat", "/crm/seatreserve", "/crm/calendar", "/crm/promise", "/crm/documents"] },
  { key: "leads", label: "Leads & Admissions", icon: Users2, routes: ["/crm/leads", "/crm/pipeline", "/crm/reception", "/crm/token", "/crm/mobility", "/counselling", "/crm/machine", "/crm/cohorts", "/crm/referrals", "/crm/partners", "/crm/playbook", "/crm/rast"] },
  { key: "students", label: "Students & Academics", icon: GraduationCap, routes: ["/students", "/attendance", "/approvals"] },
  { key: "marketing", label: "Marketing & Outreach", icon: Megaphone, routes: ["/crm/seminars", "/crm/outreach", "/crm/schools", "/crm/meta", "/crm/whatsapp", "/crm/waconnect", "/crm/campaigns", "/crm/events", "/crm/eventcmd", "/crm/bells", "/crm/vidarbha"] },
  { key: "finance", label: "Finance & People", icon: Wallet, routes: ["/finance", "/crm/payroll", "/crm/reconcile", "/crm/earnings", "/crm/incentives", "/staff", "/team"] },
  { key: "leadership", label: "Leadership & System", icon: Gauge, routes: ["/crm/director", "/crm/warroom", "/crm/review", "/crm/reports", "/settings", "/crm/trash"] },
];

function buildClusters(items) {
  const idx = {};
  CLUSTER_DEFS.forEach((c, i) => c.routes.forEach((r) => { idx[r] = i; }));
  const buckets = CLUSTER_DEFS.map((c) => ({ key: c.key, label: c.label, icon: c.icon, items: [] }));
  const more = { key: "more", label: "More", icon: Menu, items: [] };
  items.forEach((n) => {
    const i = idx[n.to];
    if (i == null) more.items.push(n); else buckets[i].items.push(n);
  });
  const out = buckets.filter((b) => b.items.length);
  if (more.items.length) out.push(more);
  return out;
}

function activeToFor(items, pathname) {
  let best = "";
  items.forEach((n) => {
    if (n.to === "/") { if (pathname === "/" && best === "") best = "/"; return; }
    if (pathname === n.to || pathname.startsWith(n.to + "/")) { if (n.to.length > best.length) best = n.to; }
  });
  return best;
}

const NAV = [
  { to: "/", label: "Command Center", icon: LayoutDashboard, testid: "nav-command" },
  { to: "/students", label: "Students & Batches", icon: GraduationCap, testid: "nav-students" },
  { to: "/counselling", label: "Counselling", icon: CalendarClock, testid: "nav-counselling" },
  { to: "/finance", label: "Fee & Receipts", icon: Wallet, testid: "nav-finance" },
  { to: "/attendance", label: "Attendance", icon: CalendarCheck2, testid: "nav-attendance" },
  { to: "/approvals", label: "Approvals", icon: ClipboardCheck, testid: "nav-approvals" },
  { to: "/tasks", label: "Tasks", icon: CheckSquare, testid: "nav-tasks" },
  { to: "/staff", label: "Staff Scorecards", icon: UserCog, testid: "nav-staff" },
  { to: "/team", label: "Team & Presence", icon: Radio, testid: "nav-team" },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

const MOBILE_TABS = [
  { to: "/", label: "Overview", icon: LayoutDashboard },
  { to: "/crm/leads", label: "Leads", icon: Users2 },
  { to: "/students", label: "Students", icon: GraduationCap },
  { to: "/finance", label: "Fees", icon: Wallet },
  { to: "/approvals", label: "More", icon: Menu },
];

// Admissions CRM (unified shell) — one login opens these via the rcrm bridge.
const CRM_NAV = [
  { to: "/crm", label: "Dashboard", icon: LayoutDashboard, testid: "nav-crm-home" },
  { to: "/crm/myleads", label: "My Leads", icon: Users2, testid: "nav-crm-myleads" },
  { to: "/crm/queue", label: "Today's Calls", icon: PhoneCall, testid: "nav-crm-queue" },
  { to: "/crm/followups", label: "Follow-ups", icon: CalendarClock, testid: "nav-crm-followups" },
  { to: "/crm/seatreserve", label: "Seat Reserve", icon: CalendarDays, testid: "nav-crm-seatreserve" },
  { to: "/crm/calendar", label: "Counselling Calendar", icon: CalendarDays, testid: "nav-crm-calendar" },
  { to: "/crm/chat", label: "Team Chat", icon: MessageCircle, testid: "nav-crm-chat" },
  { to: "/crm/documents", label: "Documents", icon: FolderOpen, testid: "nav-crm-documents" },
  { to: "/crm/director", label: "Director's Dashboard", icon: Gauge, testid: "nav-crm-director" },
  { to: "/crm/leads", label: "Leads", icon: Users2, testid: "nav-crm-leads" },
  { to: "/crm/pipeline", label: "Pipeline", icon: GitBranch, testid: "nav-crm-pipeline" },
  { to: "/crm/mobility", label: "Student Mobility", icon: MapPin, testid: "nav-crm-mobility" },
  { to: "/crm/reception", label: "Reception", icon: ClipboardList, testid: "nav-crm-reception" },
  { to: "/crm/token", label: "Token Board", icon: Tv, testid: "nav-crm-token" },
  { to: "/crm/eventcmd", label: "Event Command", icon: PartyPopper, testid: "nav-crm-eventcmd" },
  { to: "/crm/whatsapp", label: "WhatsApp", icon: MessageCircle, testid: "nav-crm-whatsapp" },
  { to: "/crm/meta", label: "Meta Lead Ads", icon: Megaphone, testid: "nav-crm-meta" },
  { to: "/crm/waconnect", label: "WhatsApp Connect", icon: MessageCircle, testid: "nav-crm-waconnect" },
  { to: "/crm/seminars", label: "School Seminars", icon: Presentation, testid: "nav-crm-seminars" },
  { to: "/crm/outreach", label: "School Outreach", icon: Map, testid: "nav-crm-outreach" },
  { to: "/crm/bells", label: "Two Bells", icon: BellRing, testid: "nav-crm-bells" },
  { to: "/crm/rast", label: "RAST Sessions", icon: FlaskConical, testid: "nav-crm-rast" },
  { to: "/crm/events", label: "Events & Tickets", icon: CalendarDays, testid: "nav-crm-events" },
  { to: "/crm/schools", label: "School Machine", icon: School, testid: "nav-crm-schools" },
  { to: "/crm/cohorts", label: "Reactivation", icon: RefreshCw, testid: "nav-crm-cohorts" },
  { to: "/crm/playbook", label: "Objections", icon: MessageCircle, testid: "nav-crm-playbook" },
  { to: "/crm/earnings", label: "Earnings", icon: Coins, testid: "nav-crm-earnings" },
  { to: "/crm/referrals", label: "Referrals", icon: Gift, testid: "nav-crm-referrals" },
  { to: "/crm/partners", label: "Partners & Credits", icon: Gift, testid: "nav-crm-partners" },
  { to: "/crm/machine", label: "Lead Machine", icon: Megaphone, testid: "nav-crm-machine" },
  { to: "/crm/campaigns", label: "Campaigns & ROI", icon: TrendingUp, testid: "nav-crm-campaigns" },
  { to: "/crm/warroom", label: "War Room", icon: Crosshair, testid: "nav-crm-warroom" },
  { to: "/crm/payroll", label: "Payroll", icon: Wallet, testid: "nav-crm-payroll" },
  { to: "/crm/review", label: "Team Review", icon: ClipboardCheck, testid: "nav-crm-review" },
  { to: "/crm/promise", label: "Promise Keeper", icon: UserPlus, testid: "nav-crm-promise" },
  { to: "/crm/reconcile", label: "Fees Reconcile", icon: Wallet, testid: "nav-crm-reconcile" },
  { to: "/crm/vidarbha", label: "Vidarbha", icon: MapPin, testid: "nav-crm-vidarbha" },
  { to: "/crm/reports", label: "CRM Reports", icon: BarChart3, testid: "nav-crm-reports" },
  { to: "/crm/incentives", label: "Incentives", icon: Coins, testid: "nav-crm-incentives" },
  { to: "/crm/trash", label: "Trash", icon: Trash2, testid: "nav-crm-trash" },
];

// Per-role nav visibility now lives in @/lib/roles (shared with route-level guards).

const IDLE_WARN_MS = 15 * 60 * 1000;
const IDLE_LOGOUT_MS = 20 * 60 * 1000;

export default function AppShell({ children }) {
  const { user, logout, activeCenter, setActiveCenter, checkAuth } = useAuth();
  const [centers, setCenters] = useState([]);
  const [drawer, setDrawer] = useState(false);
  const [menu, setMenu] = useState(false);
  const [idleWarn, setIdleWarn] = useState(false);
  const [idleLeft, setIdleLeft] = useState(300);
  const idleReset = React.useRef(() => {});
  const navigate = useNavigate();
  const allCenters = ["DIRECTOR", "CO_DIRECTOR", "HEAD_OFFICE"].includes(user?.role);
  const perms = usePerms();
  const nav = NAV.filter((n) => canAccess(n.to, user?.role, perms.os));
  const crmAllowed = hasCrm(user?.role);
  const [crmRole, setCrmRole] = useState(() => localStorage.getItem("rcrm_role") || "");
  useEffect(() => {
    if (!crmAllowed) return;
    let alive = true;
    (async () => {
      const RB = `${process.env.REACT_APP_BACKEND_URL}/api/rcrm`;
      try {
        const r = await axios.post(`${RB}/auth/bridge`, {}, { withCredentials: true });
        const t = r.data.token; localStorage.setItem("rcrm_token", t);
        const me = await axios.get(`${RB}/me`, { headers: { Authorization: `Bearer ${t}` } });
        if (alive) { setCrmRole(me.data.role); localStorage.setItem("rcrm_role", me.data.role); localStorage.setItem("rcrm_uid", me.data.id); }
      } catch { /* backend still enforces RBAC; sidebar shows all if role unknown */ }
    })();
    return () => { alive = false; };
  }, [crmAllowed]);
  const crmNav = !crmAllowed ? [] : CRM_NAV.filter((n) => {
    const key = n.to === "/crm" ? "home" : n.to.replace("/crm/", "");
    if (key === "myleads" && (crmRole === "Director" || user?.role === "DIRECTOR")) return false;
    return !crmRole || canAccessCrm(key, crmRole, perms.crm);
  });
  // One merged sidebar in the Director's order: Dashboard → My Leads → Today's Calls → Follow-ups → Counselling →
  // Seat Reserve → Team Chat → remaining OS screens → remaining CRM screens (Tasks placed right after Pipeline).
  const sidebar = React.useMemo(() => {
    const all = [...crmNav, ...nav];
    const byTo = Object.fromEntries(all.map((n) => [n.to, n]));
    const head = ["/crm", "/crm/myleads", "/crm/queue", "/crm/followups", "/counselling", "/crm/seatreserve", "/crm/chat"];
    const used = new Set([...head, "/tasks"]);
    const out = head.map((t) => byTo[t]).filter(Boolean);
    nav.forEach((n) => { if (!used.has(n.to)) { out.push(n); used.add(n.to); } });
    crmNav.forEach((n) => {
      if (used.has(n.to)) return;
      out.push(n); used.add(n.to);
      if (n.to === "/crm/pipeline" && byTo["/tasks"]) out.push(byTo["/tasks"]);
    });
    if (byTo["/tasks"] && !out.includes(byTo["/tasks"])) out.push(byTo["/tasks"]);
    return out;
  }, [crmNav, nav]);
  const { pathname } = useLocation();
  // A short menu (callers / counsellors) is shown flat — no group headers to open.
  const flatNav = sidebar.length <= 8;
  const clusters = React.useMemo(() => buildClusters(sidebar), [sidebar]);
  const activeTo = React.useMemo(() => activeToFor(sidebar, pathname), [sidebar, pathname]);
  const activeClusterKey = React.useMemo(() => {
    const idx = {};
    CLUSTER_DEFS.forEach((c, i) => c.routes.forEach((r) => { idx[r] = i; }));
    return activeTo && idx[activeTo] != null ? CLUSTER_DEFS[idx[activeTo]].key : "work";
  }, [activeTo]);
  const [openKey, setOpenKey] = useState(activeClusterKey);
  useEffect(() => { setOpenKey(activeClusterKey); }, [activeClusterKey]);
  const mobileTabs = MOBILE_TABS.filter((t) => t.label === "More" || (t.to.startsWith("/crm") ? crmAllowed : canAccess(t.to, user?.role, perms.os)));
  const [bellTick, setBellTick] = useState(0);
  const [impUsers, setImpUsers] = useState([]);
  useEffect(() => {
    if (user?.role === "DIRECTOR" && !user?.impersonator_id) {
      api.get("/auth/impersonate/users").then((r) => setImpUsers(r.data.items || [])).catch(() => {});
    }
  }, [user]);
  const startImpersonate = async (id) => {
    if (!id) return;
    try {
      await api.post("/auth/impersonate/start", { target_id: id });
      ["rcrm_token", "rcrm_role", "rcrm_uid"].forEach((k) => localStorage.removeItem(k));
      window.location.href = "/";
    } catch { toast.error("Could not switch account"); }
  };
  const stopImpersonate = async () => {
    try {
      await api.post("/auth/impersonate/stop");
      ["rcrm_token", "rcrm_role", "rcrm_uid"].forEach((k) => localStorage.removeItem(k));
      window.location.href = "/";
    } catch { toast.error("Could not exit"); }
  };
  useFollowupAlarms(crmAllowed, navigate, () => setBellTick((t) => t + 1));

  useEffect(() => {
    api.get("/settings/centers").then((r) => setCenters(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const beat = () => api.post("/presence/heartbeat").catch(() => {});
    beat();
    const t = setInterval(beat, 60000);
    return () => clearInterval(t);
  }, []);

  // Idle auto-logout: warn at 15m, sign out at 20m (keeps presence accurate)
  useEffect(() => {
    let warnT, outT;
    const reset = () => {
      clearTimeout(warnT); clearTimeout(outT);
      setIdleWarn(false);
      warnT = setTimeout(() => setIdleWarn(true), IDLE_WARN_MS);
      outT = setTimeout(() => logout(), IDLE_LOGOUT_MS);
    };
    idleReset.current = reset;
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => { clearTimeout(warnT); clearTimeout(outT); events.forEach((e) => window.removeEventListener(e, reset)); };
  }, [logout]);

  useEffect(() => {
    if (!idleWarn) return;
    setIdleLeft(Math.round((IDLE_LOGOUT_MS - IDLE_WARN_MS) / 1000));
    const iv = setInterval(() => setIdleLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(iv);
  }, [idleWarn]);

  return (
    <div className="min-h-screen bg-[#FBF7F3]">
      {/* Header */}
      <header
        className="fixed top-0 left-0 right-0 h-14 bg-[#C2410C] text-white flex items-center justify-between px-3 sm:px-4 z-40"
        style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.15)" }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <button className="lg:hidden p-1.5" onClick={() => setDrawer(true)} data-testid="menu-btn">
            <Menu size={20} />
          </button>
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-[#FFD700] text-[#C2410C] flex items-center justify-center font-extrabold text-sm shrink-0">
              R
            </div>
            <div className="min-w-0 leading-tight">
              <p className="text-sm font-bold truncate">The Rankers Academy</p>
              <p className="text-[10px] text-white/70 -mt-0.5 hidden sm:block">Rankers OS · Operations</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          {crmAllowed && <LeadSearch />}
          {crmAllowed && <OfflineStatus />}
          <InstallAppButton className="hidden sm:inline-flex" />
          {user?.impersonator_id ? (
            <div data-testid="impersonation-banner" className="flex items-center gap-2 bg-[#FFD700] text-[#1A1A2E] rounded-full pl-3 pr-1.5 py-1 text-xs font-semibold max-w-[55vw]">
              <UserCog size={14} />
              <span className="truncate">Viewing as {user.name}</span>
              <button data-testid="impersonation-exit" onClick={stopImpersonate} className="bg-[#1A1A2E] text-white rounded-full px-2.5 py-1 text-[11px] font-semibold hover:bg-black">Exit</button>
            </div>
          ) : (user?.role === "DIRECTOR" && (
            <select data-testid="view-as-select" value="" onChange={(e) => startImpersonate(e.target.value)} title="View as a staff member"
              className="hidden lg:block text-xs rounded-lg bg-white/15 text-white border border-white/25 px-2 py-1.5 focus:outline-none cursor-pointer max-w-[140px]">
              <option value="" className="text-black">View as…</option>
              {impUsers.map((u) => <option key={u.id} value={u.id} className="text-black">{u.name} · {u.role}</option>)}
            </select>
          ))}
          {allCenters && (
            <div className="relative hidden sm:block">
              <select
                data-testid="center-switcher"
                value={activeCenter}
                onChange={(e) => setActiveCenter(e.target.value)}
                className="appearance-none bg-white/15 text-white text-xs rounded-lg pl-3 pr-7 py-1.5 border border-white/20 focus:outline-none cursor-pointer"
              >
                <option value="all" className="text-black">All Centers</option>
                {centers.map((c) => (
                  <option key={c.id} value={c.id} className="text-black">{c.name}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-2 top-2 pointer-events-none" />
            </div>
          )}
          <NotificationBell refreshKey={bellTick} />
          <div className="relative">
            <button
              onClick={() => setMenu((m) => !m)}
              className="flex items-center gap-2 pl-1"
              data-testid="avatar-btn"
            >
              <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-xs font-semibold">
                {user?.name?.[0] || "U"}
              </div>
              <span className="hidden md:block text-xs text-left leading-tight">
                <span className="block font-semibold">{user?.name}</span>
                <span className="block text-white/60 text-[10px]">{ROLE_LABELS[user?.role]}</span>
              </span>
            </button>
            {menu && (
              <div className="absolute right-0 mt-2 w-48 bg-white text-[#1A1A2E] rounded-lg border border-[#FDEBDD] shadow-lg py-1 z-50">
                <div className="px-3 py-2 border-b border-[#FDEBDD]">
                  <p className="text-xs font-semibold truncate">{user?.email}</p>
                  <p className="text-[10px] text-[#6B7280]">{ROLE_LABELS[user?.role]}</p>
                </div>
                <button
                  onClick={logout}
                  data-testid="logout-btn"
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#FFEDD5] text-[#C0392B]"
                >
                  <LogOut size={15} /> Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col fixed top-14 left-0 bottom-0 w-60 bg-white border-r border-[#FDEBDD] py-3 px-2.5 gap-0.5 overflow-y-auto" data-testid="sidebar-nav" data-flat={flatNav ? "true" : "false"}>
        {flatNav ? sidebar.map((n) => <NavItem key={n.to} {...n} />) : <ClusterNav clusters={clusters} openKey={openKey} setOpenKey={setOpenKey} activeTo={activeTo} />}
        <div className="mt-auto px-2 pt-3 text-[10px] text-[#6B7280]">
          The Rankers Academy · Nagpur
        </div>
      </aside>

      {/* Mobile drawer */}
      {drawer && (
        <div className="lg:hidden fixed inset-0 z-50" onClick={() => setDrawer(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="absolute top-0 left-0 bottom-0 w-64 bg-white p-3 overflow-y-auto overscroll-contain animate-slideIn"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3 px-1">
              <span className="font-bold text-sm text-[#C2410C]">Menu</span>
              <button onClick={() => setDrawer(false)}><X size={18} /></button>
            </div>
            {allCenters && (
              <select
                value={activeCenter}
                onChange={(e) => setActiveCenter(e.target.value)}
                className="w-full mb-3 text-sm rounded-lg border border-[#FDEBDD] px-3 py-2"
                data-testid="center-switcher-mobile"
              >
                <option value="all">All Centers</option>
                {centers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
            {flatNav ? sidebar.map((n) => <div key={n.to} onClick={() => setDrawer(false)}><NavItem {...n} /></div>) : <ClusterNav clusters={clusters} openKey={openKey} setOpenKey={setOpenKey} activeTo={activeTo} onItemClick={() => setDrawer(false)} />}
          </div>
        </div>
      )}

      {/* Main */}
      <main className="pt-14 lg:pl-60 pb-20 lg:pb-6 min-h-screen">
        <div className="max-w-[1400px] mx-auto px-3 sm:px-5 py-4 sm:py-5 animate-fadeUp">{children}</div>
      </main>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 h-16 bg-white border-t border-[#FDEBDD] flex items-center justify-around z-40">
        {mobileTabs.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            onClick={t.label === "More" ? (e) => { e.preventDefault(); setDrawer(true); } : undefined}
            className={({ isActive }) =>
              `flex flex-col items-center gap-0.5 text-[10px] ${
                isActive && t.label !== "More" ? "text-[#C2410C]" : "text-[#6B7280]"
              }`
            }
            data-testid={`mtab-${t.label.toLowerCase()}`}
          >
            <t.icon size={20} />
            {t.label}
          </NavLink>
        ))}
      </nav>

      {/* Idle auto-logout warning */}
      {idleWarn && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4" data-testid="idle-warning">
          <div className="bg-white rounded-2xl border border-[#FDEBDD] shadow-xl w-full max-w-sm p-5 text-center">
            <div className="w-11 h-11 rounded-full bg-[#FFEDD5] text-[#C2410C] flex items-center justify-center mx-auto mb-3">
              <LogOut size={20} />
            </div>
            <p className="text-base font-bold text-[#1A1A2E]">Still there?</p>
            <p className="text-sm text-[#6B7280] mt-1">
              You've been inactive for a while. You'll be signed out in{" "}
              <span className="font-semibold text-[#C2410C] tnum" data-testid="idle-countdown">
                {Math.floor(idleLeft / 60)}:{String(idleLeft % 60).padStart(2, "0")}
              </span>{" "}to keep attendance accurate.
            </p>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => logout()}
                data-testid="idle-signout-btn"
                className="flex-1 text-sm font-medium px-3 py-2 rounded-lg border border-[#FDEBDD] text-[#6B7280] hover:bg-[#FBF7F3]"
              >
                Sign out now
              </button>
              <button
                onClick={() => idleReset.current()}
                data-testid="idle-stay-btn"
                className="flex-1 text-sm font-medium px-3 py-2 rounded-lg bg-[#C2410C] text-white hover:bg-[#9A3412]"
              >
                Stay signed in
              </button>
            </div>
          </div>
        </div>
      )}
      {user?.must_change_password && !user?.impersonator_id && <ForcePasswordChange onDone={checkAuth} />}
    </div>
  );
}

function ForcePasswordChange({ onDone }) {
  const [nw, setNw] = useState("");
  const [nw2, setNw2] = useState("");
  const [cur, setCur] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (nw.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (nw !== nw2) { toast.error("New passwords do not match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password updated");
      await onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not change password"); }
    finally { setSaving(false); }
  };
  const inp = "mt-1 w-full rounded-lg border border-[#FDEBDD] px-3 py-2.5 text-sm focus:outline-none focus:border-[#C2410C] bg-white";
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 px-4" data-testid="force-password-change">
      <div className="bg-white rounded-2xl border border-[#FDEBDD] shadow-xl w-full max-w-sm p-6">
        <h3 className="text-lg font-bold text-[#1A1A2E]">Set a new password</h3>
        <p className="text-sm text-[#6B7280] mt-1 mb-4">You're using a temporary password. Please choose your own password (at least 8 characters) to continue.</p>
        <form onSubmit={submit} className="space-y-3">
          <div><label className="text-xs font-medium text-[#6B7280]">Current (temporary) password</label>
            <input type="password" data-testid="fpc-current" value={cur} onChange={(e) => setCur(e.target.value)} required className={inp} /></div>
          <div><label className="text-xs font-medium text-[#6B7280]">New password</label>
            <input type="password" data-testid="fpc-new" value={nw} onChange={(e) => setNw(e.target.value)} required className={inp} /></div>
          <div><label className="text-xs font-medium text-[#6B7280]">Confirm new password</label>
            <input type="password" data-testid="fpc-confirm" value={nw2} onChange={(e) => setNw2(e.target.value)} required className={inp} /></div>
          <button type="submit" disabled={saving} data-testid="fpc-submit" className="w-full bg-[#C2410C] hover:bg-[#9A3412] text-white py-2.5 rounded-lg text-sm font-semibold disabled:opacity-60">
            {saving ? "Saving…" : "Set password & continue"}
          </button>
        </form>
      </div>
    </div>
  );
}

function ClusterNav({ clusters, openKey, setOpenKey, activeTo, onItemClick }) {
  return (
    <>
      {clusters.map((c) => {
        const open = openKey === c.key;
        const hasActive = c.items.some((n) => n.to === activeTo);
        const Icon = c.icon;
        return (
          <div key={c.key} className="mb-0.5">
            <button
              data-testid={`navgroup-${c.key}`}
              onClick={() => setOpenKey(open ? null : c.key)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-semibold transition-colors hover:bg-[#FBF7F3] ${hasActive ? "text-[#C2410C]" : "text-[#1A1A2E]"}`}
            >
              <Icon size={17} />
              <span className="flex-1 text-left">{c.label}</span>
              {hasActive && !open && <span className="w-1.5 h-1.5 rounded-full bg-[#C2410C]" />}
              <ChevronDown size={15} className={`transition-transform duration-200 ${open ? "" : "-rotate-90"}`} />
            </button>
            {open && (
              <div className="ml-[18px] pl-2 border-l border-[#FDEBDD] mt-0.5 flex flex-col gap-0.5">
                {c.items.map((n) => (
                  <div key={n.to} onClick={onItemClick}><NavItem {...n} /></div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

function NavItem({ to, label, icon: Icon, testid }) {
  return (
    <NavLink
      to={to}
      end={to === "/" || to === "/crm"}
      data-testid={testid}
      className={({ isActive }) =>
        `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
          isActive
            ? "bg-[#FFEDD5] text-[#C2410C]"
            : "text-[#6B7280] hover:bg-[#FBF7F3] hover:text-[#1A1A2E]"
        }`
      }
    >
      <Icon size={17} />
      {label}
    </NavLink>
  );
}

function timeAgoShort(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "now";
  if (diff < 3600) return Math.floor(diff / 60) + "m";
  if (diff < 86400) return Math.floor(diff / 3600) + "h";
  return Math.floor(diff / 86400) + "d";
}

const RB = `${process.env.REACT_APP_BACKEND_URL}/api/rcrm`;
const CRM_KIND_TITLE = { team_work_report: "Team review ready", team_appreciation: "An appreciation for your work", missed_followup_digest: "7 PM missed follow-up report", followup: "Follow-up reminder", alarm: "Promised callback", alert: "Admissions CRM", nurture: "Moved to nurture", myleads: "My Leads", assignment: "Work assigned to you", instruction: "Instruction from management", chat: "Chat mention", tab_move: "Lead moved on your board" };
// unread notifications of these kinds keep the bell blinking until they are opened / marked read
const URGENT_KINDS = new Set(["assignment", "instruction", "task_assigned", "app_lead_assigned"]);
const crmHdr = () => { const t = localStorage.getItem("rcrm_token"); return t ? { headers: { Authorization: `Bearer ${t}` } } : null; };

function NotificationBell({ refreshKey = 0 }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const urgent = items.some((n) => !n.read && URGENT_KINDS.has(n.kind));
  const load = async () => {
    const h = crmHdr();
    const [legacy, crm] = await Promise.all([
      api.get("/notifications").catch(() => null),
      h ? axios.get(`${RB}/notifications`, h).catch(() => null) : Promise.resolve(null),
    ]);
    const a = legacy ? (legacy.data.items || []).map((n) => ({ ...n, kind: n.type })) : [];
    const b = crm ? (crm.data.items || []).map((n) => ({ id: n.id, title: CRM_KIND_TITLE[n.kind] || "Admissions CRM", body: n.text, ts: n.at, read: !!n.read, crm: true, kind: n.kind, lead_id: n.lead_id, conversation_id: n.conversation_id, digest_date: n.digest_date, report_id: n.report_id })) : [];
    const all = [...a, ...b].sort((x, y) => new Date(y.ts || 0) - new Date(x.ts || 0)).slice(0, 60);
    setItems(all);
    setUnread(all.filter((n) => !n.read).length);
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  useEffect(() => { if (refreshKey) load(); }, [refreshKey]);
  // instant bell: the server pushes notify:new the moment a notification is written
  const pushTimer = React.useRef(null);
  useCrmSocket("notify:new", () => { clearTimeout(pushTimer.current); pushTimer.current = setTimeout(load, 400); });
  const markAll = async () => {
    const h = crmHdr();
    await Promise.all([api.post("/notifications/read-all").catch(() => {}), h ? axios.post(`${RB}/notifications/read-all`, {}, h).catch(() => {}) : null]);
    load();
  };
  const clickItem = async (n) => {
    if (!n.read) {
      if (n.crm) { const h = crmHdr(); if (h) await axios.post(`${RB}/notifications/${n.id}/read`, {}, h).catch(() => {}); }
      else await api.post(`/notifications/${n.id}/read`).catch(() => {});
      load();
    }
    if (n.crm && n.kind === "followup") { setOpen(false); navigate("/crm/followups"); }
    const crmRole = localStorage.getItem("rcrm_role") || "";
    if (n.crm && n.kind === "assignment") { setOpen(false); navigate(n.lead_id ? leadLink(crmRole, n.lead_id) : "/crm/queue"); }
    if (n.crm && n.kind === "tab_move") { setOpen(false); navigate(n.lead_id ? leadLink(crmRole, n.lead_id) : "/crm/myleads"); }
    if (n.crm && ["wa_reply", "wa_assign", "wa_sla"].includes(n.kind)) { setOpen(false); navigate(n.conversation_id ? `/crm/whatsapp?tab=inbox&c=${n.conversation_id}` : n.lead_id ? leadLink(crmRole, n.lead_id) : "/crm/queue"); }
    if (n.kind === "task_assigned") { setOpen(false); navigate("/tasks"); }
    if (n.crm && n.kind === "team_work_report") {
      setOpen(false); navigate(`/crm/director?view=team&mode=reviews&report=${encodeURIComponent(n.report_id)}`);
    }
    if (n.crm && n.kind === "missed_followup_digest") {
      setOpen(false); navigate(`/crm/followups?digest=${encodeURIComponent(n.digest_date)}`);
    }
  };
  return (
    <div className="relative">
      <button onClick={() => { setOpen((o) => !o); load(); }} aria-label={urgent ? "Notifications — new work assigned" : "Notifications"} aria-expanded={open} data-urgent={urgent ? "true" : "false"} className={`min-w-11 min-h-11 inline-flex items-center justify-center relative rounded-full ${urgent ? "notif-blink" : ""}`} data-testid="notif-btn">
        <Bell size={18} className={urgent ? "notif-blink-icon" : ""} />
        {unread > 0 && (
          <span data-testid="notif-badge" className={`absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 text-[9px] font-bold rounded-full flex items-center justify-center ${urgent ? "bg-[#DC2626] text-white notif-blink-badge" : "bg-[#FFD700] text-[#9A3412]"}`}>
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="fixed left-3 right-3 top-16 sm:absolute sm:left-auto sm:top-auto sm:right-0 sm:mt-2 sm:w-96 bg-white text-[#1A1A2E] rounded-xl border border-[#FDEBDD] shadow-xl z-50" data-testid="notif-panel">
            <div className="flex items-center justify-between px-3 py-2 border-b border-[#FDEBDD]">
              <span className="text-xs font-semibold">Notifications</span>
              {unread > 0 && <button onClick={markAll} data-testid="notif-read-all" className="text-xs text-[#C2410C] font-medium min-h-11 px-2">Mark all read</button>}
            </div>
            <div className="max-h-[65dvh] overflow-y-auto overscroll-contain">
              {items.length ? items.map((n) => (
                <button key={n.id} onClick={() => clickItem(n)} data-testid={`notif-${n.id}`} className={`w-full min-h-16 text-left px-4 py-3 border-b border-[#FDEBDD] hover:bg-[#FBF7F3] ${n.read ? "" : "bg-[#FFEDD5]/50"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold">{n.kind === "followup" ? "⏰ " : ""}{URGENT_KINDS.has(n.kind) && !n.read ? <span className="inline-block w-2 h-2 rounded-full bg-[#DC2626] mr-1.5 align-middle notif-blink-badge" data-testid={`notif-urgent-${n.id}`} /> : null}{n.title}</span>
                    <span className="text-[10px] text-[#6B7280] shrink-0">{timeAgoShort(n.ts)}{!n.read && <span className="inline-block ml-1 w-1.5 h-1.5 bg-[#C2410C] rounded-full align-middle" />}</span>
                  </div>
                  <p className="text-xs leading-relaxed text-[#6B7280] mt-1">{n.body}</p>
                  {n.kind === "team_work_report" && <span className="inline-block mt-2 text-xs font-semibold text-[#9A3412]">Review team work & appreciations →</span>}
                  {n.kind === "missed_followup_digest" && <span className="inline-block mt-2 text-xs font-semibold text-[#9A3412]">View caller breakdown →</span>}
                </button>
              )) : <p className="text-xs text-[#6B7280] px-3 py-6 text-center">No notifications yet</p>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
